import { InfraToken, OpenStackApiClient } from 'openstack-api-client';
import * as R from 'ramda';
import router from 'system/router';
import { CryptoHelper } from 'utils/CryptoHelper';
import { McsErrors } from 'utils/McsErrors/McsErrors';
import { ReduxHelper } from 'utils/ReduxHelper';
import { ReduxTypeUtils } from 'utils/ReduxTypeUtils';
import { reflectStorage } from 'utils/ReflectStorage';
import { RouterPidHelper } from 'utils/RouterPidHelper';

import {
    CurrentUserModule,
    INITIAL_STATE,
} from 'reactApp/modules/CurrentUserModule/CurrentUserModule';
import { CurrentUserApi } from 'reactApp/modules/CurrentUserModule/CurrentUserModule.api';
import { CurrentUserSelectors } from 'reactApp/modules/CurrentUserModule/CurrentUserModule.selectors';
import * as CurrentUserTypes from 'reactApp/modules/CurrentUserModule/CurrentUserModule.types';

enum Actions {
    signIn,
    signUp,
}

// TODO: sign in signup no longer in user api, move it to auth module
const signUp = (
    data:
        | CurrentUserTypes.SignInSignUpData
        | CurrentUserTypes.PartnerSignupData,
    params?: CurrentUserTypes.SignUpParams,
): ReduxTypeUtils.ThunkResult<Promise<
    CurrentUserTypes.UserApiEntity
>> => async (dispatch, getState) => {
    try {
        const user = await CurrentUserApi.signUp(data, params);
        dispatch(CurrentUserModule.mergeModuleState(user));

        if (data.phone) {
            const encryptedPhone = CryptoHelper.encrypt(data.phone, data.email);
            reflectStorage.set('signupPhone', encryptedPhone.toString());
        }

        dispatch(obtainSelectedPid());

        if (params && params.isPartner) {
            // @ts-ignore
            gtag('event', 'MainEvent', {
                // в этом файле своя реализация gtag, чтоб не возникало циклического импорта
                event_category: 'partnership',
                event_action: 'RegistrationStart',
                event_label: CurrentUserSelectors.getPid(getState()),
            });
        }

        return user;
    } catch (errorResponse) {
        throw handleAuthError(errorResponse, Actions.signUp);
    }
};

function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer && window.dataLayer.push(arguments);
}

const handleAuthError = (errorResponse, action: Actions) => {
    const failTexts = {
        [Actions.signUp]: 'Не удалось зарегистрироваться.',
        [Actions.signIn]: 'Не удалось авторизоваться.',
    };
    const wrongEmail = 'Неверный email';
    const invalidEmailText = {
        [Actions.signUp]: 'Введите валидный email',
        [Actions.signIn]: wrongEmail,
    };

    const wrongPassword = 'Неверный пароль';
    const invalidPasswordText = {
        [Actions.signUp]:
            'Пароль может быть от 8 до 40 символов, должен содержать латинские буквы, цифры или символы: ? ! ~ @ # $ % ^ & _ - + * = ; : , . / \\ | ` [ ] { } ( )',
        [Actions.signIn]: wrongPassword,
    };

    const failedActionText = failTexts[action];

    const emailExistsText = 'Такой email уже зарегистрирован';

    const error = McsErrors.getFormMessagesFromResponse(errorResponse, {
        fieldTexts: {
            default: 'Введено некорректное значение',
            email: {
                conflict: emailExistsText,
                argument_required: 'Введите email',
                argument_invalid: invalidEmailText[action],
                not_found: invalidEmailText[action],
                internal_error: invalidEmailText[action],
                unauthorized: wrongEmail,
                blocked: `Аккаунт заблокирован. ${McsErrors.CALL_SUPPORT_HTML}`,
                access_denied: `Аккаунт заблокирован. ${McsErrors.CALL_SUPPORT_HTML}`,
            },
            password: {
                argument_required: 'Введите пароль',
                access_denied: wrongPassword,
                argument_invalid: invalidPasswordText[action],
                unauthorized: wrongPassword,
            },
            captcha: McsErrors.CAPTCHA_ERROR_MESSAGES,
        },
        commonTexts: {
            default: `${failedActionText} ${McsErrors.TRY_LATER_HTML}`,
            conflict: emailExistsText,
        },
    });

    if (error.commonMessages.includes(emailExistsText)) {
        error.fieldMessages.email = emailExistsText;
        error.commonMessages = [];
    }

    return error;
};

const signIn = (
    data: CurrentUserTypes.SignInSignUpData,
    errorHandlingDisabled?: boolean,
): ReduxTypeUtils.ThunkResult<Promise<
    CurrentUserTypes.UserApiEntity
>> => async (dispatch) => {
    try {
        const user = await CurrentUserApi.signIn(data);
        dispatch(CurrentUserModule.mergeModuleState(user));

        dispatch(obtainSelectedPid());

        return user;
    } catch (errorResponse) {
        if (errorHandlingDisabled) {
            throw errorResponse;
        }
        throw handleAuthError(errorResponse, Actions.signIn);
    }
};

const signOut = (): ReduxTypeUtils.ThunkResult<Promise<void>> => async (
    dispatch,
) => {
    await CurrentUserApi.signOut();
    dispatch(CurrentUserModule.setModuleState(INITIAL_STATE));
};

const loadUser = (): ReduxTypeUtils.ThunkResult<Promise<
    CurrentUserTypes.UserApiEntity
>> => async (dispatch, getState) => {
    try {
        const user = await CurrentUserApi.loadUser();
        dispatch(CurrentUserModule.mergeModuleState(user));

        if (!CurrentUserSelectors.getPid(getState())) {
            dispatch(obtainSelectedPid());
        }

        return user;
    } catch (error) {
        if (error.error === 'UNAUTHORIZED') {
            dispatch(CurrentUserModule.setModuleState(INITIAL_STATE));
        }

        throw error;
    }
};

const editUser = (
    params: CurrentUserTypes.EditUserParams,
): ReduxTypeUtils.ThunkResult<Promise<void>> => async (dispatch) => {
    const editedProfile = R.clone(params);
    delete editedProfile.email;
    delete editedProfile.phone;

    const user = await CurrentUserApi.editUser(editedProfile);
    dispatch(CurrentUserModule.mergeModuleState(user));
};

const setPhone = (
    phone: string,
): ReduxTypeUtils.ThunkResult<Promise<void>> => async (dispatch) => {
    const user = await CurrentUserApi.editUser({ phone });
    dispatch(CurrentUserModule.mergeModuleState(user));
};

const mergeVerifiedObj = (obj: Partial<CurrentUserTypes.VerifiedObj>) => async (
    dispatch,
    getState,
) => {
    const currentObj = CurrentUserSelectors.getVerifiedObj(getState()) || {};
    dispatch(
        CurrentUserModule.mergeModuleState({
            // @ts-ignore
            verified: {
                ...currentObj,
                ...obj,
            },
        }),
    );
};

const setIsEmailVerified = (verified: boolean) => {
    return mergeVerifiedObj({
        email: verified,
    });
};

const setIsPhoneVerified = (verified: boolean) => {
    return mergeVerifiedObj({
        phone: verified,
    });
};

const setIsTfaEnabled = (tfa: boolean) =>
    CurrentUserModule.mergeModuleState({ tfa });

const editPassword = (
    params: CurrentUserTypes.PasswordChangeApiParams,
) => async () => {
    try {
        await CurrentUserApi.editPassword(params);
    } catch (errorResponse) {
        throw McsErrors.getFormMessagesFromResponse(errorResponse, {
            fieldTexts: {
                password: {
                    access_denied: 'Введен неверный пароль',
                },
                new_password: {
                    argument_invalid:
                        'Пароль может быть от 8 до 40 символов, должен содержать латинские буквы, цифры или символы: ? ! ~ @ # $ % ^ & _ - + * = ; : , . / \\ | ` [ ] { } ( )',
                },
                captcha: McsErrors.CAPTCHA_ERROR_MESSAGES,
            },
            commonTexts: {
                default: 'При попытке изменения пароля произошла ошибка',
            },
        });
    }
};

const obtainSelectedPid = () => (dispatch, getState) => {
    const uid = CurrentUserSelectors.getUid(getState());

    if (uid == null) {
        throw new Error(
            'trying to obtain selected pid when user is not loaded yet or user has not uid',
        );
    }

    const projects = CurrentUserSelectors.getCurrentAppProjects(getState());
    if (!projects.length) {
        return;
    }

    const pidsByUid = _getSelectedPidsFromLs();

    let pid = RouterPidHelper.getRoutePid() || pidsByUid[uid];

    const hasValidPidInStorage =
        Boolean(pid) && projects.some(R.propEq('pid', pid));
    if (!hasValidPidInStorage) {
        pid = _getPidOfMostInfluencedProject(projects);
    }

    pidsByUid[uid] = pid;

    _setSelectedPidsToLs(pidsByUid);

    dispatch(setSelectedPid(pid));

    return pid;
};

const _getPidOfMostInfluencedProject = (
    projects: CurrentUserTypes.ProjectShortData[],
) => {
    const hasRole = (role: string) => (
        project: CurrentUserTypes.ProjectShortData,
    ) => project.roles.includes(role);

    const firstProject =
        projects.find(hasRole('mcs_owner')) ||
        projects.find(hasRole('mcs_co_owner')) ||
        projects[0];

    return firstProject.pid;
};

type PidsByUid = { [uid: number]: string };
const _getSelectedPidsFromLs = () => {
    const localStorageKey = _getLocalStoragePidByUidKey();
    return (reflectStorage.get(localStorageKey) || {}) as PidsByUid;
};

const _setSelectedPidsToLs = (pidsByUid: PidsByUid) => {
    reflectStorage.set(_getLocalStoragePidByUidKey(), pidsByUid);
};

const _getLocalStoragePidByUidKey = () => {
    const isPartnerApp = router.isPartnerApp();
    return isPartnerApp ? 'partnerSelectedPids' : 'selectedPids';
};

const setSelectedPid = (pid: string) => (dispatch, getState) => {
    const uid = CurrentUserSelectors.getUid(getState());

    if (uid == null) {
        throw new Error(
            'trying to obtain selected pid when user is not loaded yet or user has not uid',
        );
    }

    const pidsByUid = _getSelectedPidsFromLs();

    pidsByUid[uid] = pid;

    _setSelectedPidsToLs(pidsByUid);

    dispatch(CurrentUserModule.mergeModuleState({ selectedPid: pid }));

    dispatch(removeInfraToken());
};

const loadUserProjectsOSData = () => async (dispatch) => {
    try {
        const projectsOSData = await CurrentUserApi.loadUserProjectsOSData();
        dispatch(CurrentUserModule.mergeModuleState({ projectsOSData }));
    } catch (error) {
        console.error(error);
    }
};

// todo: remove action after openstackRPC removed
const setInfraToken = (token: InfraToken) => () => {
    OpenStackApiClient.setInfraToken(token);
};

const removeInfraToken = () => () => {
    OpenStackApiClient.setInfraToken(null);
};

const setIsFirstLoginAfterEmailConfirmation = (value = true) =>
    CurrentUserModule.mergeModuleState({
        isFirstLoginAfterEmailConfirmation: value,
    });
const setIsShowPhoneVerificationDialog = (value) =>
    CurrentUserModule.mergeModuleState({
        isShowPhoneVerificationDialog: value,
    });

const forward = () => (dispatch, getState) => {
    return CurrentUserApi.forward(
        CurrentUserModule.getModuleState(getState()).email as string,
    );
};

const confirm = (code: string) => async () => {
    await CurrentUserApi.confirm(code);
    await CurrentUserApi.loadUser();
};

export const CurrentUserActions = {
    signUp,
    signIn,
    signOut,
    setIsFirstLoginAfterEmailConfirmation,
    setIsShowPhoneVerificationDialog,

    forward,
    confirm,

    loadUser,
    editUser,

    editPassword,

    setPhone,
    setIsEmailVerified,
    setIsPhoneVerified,
    setIsTfaEnabled,

    setSelectedPid,

    setInfraToken,
    removeInfraToken,

    loadUserProjectsOSData,
};

export const BoundCurrentUserActions = ReduxHelper.bindActions<
    typeof CurrentUserActions
>(CurrentUserActions);
